import axiosInstance from '../variables/api/axiosInstance';

/**
 * Affectation d'un dossier sinistre à un utilisateur, et cloche de notifications.
 *
 * La cloche est un canal DISTINCT des alertes de délais CEDEAO et de la messagerie
 * IMAP - ne pas agréger les compteurs.
 */
/**
 * Comptes de l'éditeur (OssanAsur) : support, administration technique.
 *
 * Ils portent des profils ADMIN/SUPERADMIN, donc l'habilitation qui rend
 * « affectable », et apparaissaient parmi les agents à qui confier un dossier -
 * d'où des affectations par erreur. Rien dans le modèle ne les distingue sinon
 * le domaine de leur adresse.
 *
 * Le filtre ne s'applique qu'aux listes d'AFFECTATION. Le filtre « traité par »
 * de la liste des sinistres les garde : un dossier déjà confié par erreur à l'un
 * de ces comptes doit rester retrouvable pour être réaffecté.
 */
const DOMAINE_EDITEUR = '@ossanasur.com';
export const estCompteEditeur = (utilisateur) =>
  (utilisateur?.email || '').trim().toLowerCase().endsWith(DOMAINE_EDITEUR);
export const sansComptesEditeur = (utilisateurs) =>
  (utilisateurs || []).filter((u) => !estCompteEditeur(u));

export const affectationService = {

  /**
   * Agents à qui confier un dossier, avec leur charge de travail actuelle.
   *
   * `sinistreTrackingId` est facultatif : quand il est fourni, la réponse signale
   * (`gereLeBureau`) qui suit le bureau homologue de ce dossier. La liste n'est
   * PAS filtrée pour autant - c'est un repère, et confier le dossier à quelqu'un
   * d'autre reste normal, notamment quand le titulaire est absent.
   */
  utilisateursAffectables: (sinistreTrackingId) =>
    axiosInstance.get('/v1/sinistres/utilisateurs-affectables',
      sinistreTrackingId ? { params: { sinistre: sinistreTrackingId } } : undefined),

  /** Confie le dossier à un utilisateur. Le notifie et historise l'affectation. */
  affecter: (sinistreTrackingId, { redacteurTrackingId, consigne }) =>
    axiosInstance.patch(`/v1/sinistres/${sinistreTrackingId}/assigner`, {
      redacteurTrackingId, consigne,
    }),

  /**
   * L'agent qui traite le dossier le confie à un collègue, avec un motif.
   * Ouvert au titulaire du dossier sans SINISTRE_ASSIGN : c'est le pendant de la
   * prise en charge par soi-même dans les affectations en attente.
   */
  reaffecter: (sinistreTrackingId, { redacteurTrackingId, motif }) =>
    axiosInstance.post(`/v1/sinistres/${sinistreTrackingId}/reaffecter`, {
      redacteurTrackingId, motif,
    }),

  /** Le destinataire accuse réception du dossier qu'on lui a confié. */
  prendreEnCharge: (sinistreTrackingId) =>
    axiosInstance.post(`/v1/sinistres/${sinistreTrackingId}/prendre-en-charge`),

  /** Trajectoire du dossier : qui l'a confié à qui, quand, avec quel délai de réaction. */
  historique: (sinistreTrackingId) =>
    axiosInstance.get(`/v1/sinistres/${sinistreTrackingId}/affectations`),
};

/**
 * Affectations en attente (nom technique historique : corbeille) : les dossiers
 * en instance de validation que personne ne s'est vu confier.
 *
 * Distincte de la file des déclarations mobiles (`mobileService.declarations`),
 * qui ne montre que le canal MOBILE et sert à VALIDER. Ici on montre les deux
 * canaux et l'acte est de PRENDRE - deux questions différentes, deux écrans.
 */
export const corbeilleService = {

  /** Le plus ancien d'abord : une file se vide dans l'ordre d'arrivée. */
  liste: (params = {}) =>
    axiosInstance.get('/v1/sinistres/corbeille', { params: { page: 0, size: 20, ...params } }),

  /** Compteur de la pastille du menu. Silencieux : un échec de sondage n'alerte pas. */
  compteur: () =>
    axiosInstance.get('/v1/sinistres/corbeille/compteur', { skipErrorToast: true }),

  /**
   * L'agent prend le dossier pour lui.
   *
   * Le 400 « X a pris ce dossier avant vous » est un cas NORMAL, pas une panne :
   * deux rédacteurs peuvent cliquer sur la même ligne. L'appelant l'affiche et
   * recharge la file.
   */
  mAffecter: (sinistreTrackingId) =>
    axiosInstance.post(`/v1/sinistres/${sinistreTrackingId}/m-affecter`, null,
      { skipErrorToast: true }),
};

export const notificationService = {

  liste: (params = {}) =>
    axiosInstance.get('/v1/notifications', { params: { page: 0, size: 20, ...params } }),

  /** Compteur du badge. skipErrorToast : un échec de poll ne doit pas alerter l'utilisateur. */
  compterNonLues: () =>
    axiosInstance.get('/v1/notifications/non-lues', { skipErrorToast: true }),

  marquerLu: (notificationTrackingId) =>
    axiosInstance.patch(`/v1/notifications/${notificationTrackingId}/lu`),

  marquerToutLu: () =>
    axiosInstance.patch('/v1/notifications/tout-lu'),
};
